import React, { useEffect, useRef, useState } from 'react'
import { X, Plus, Sparkles } from 'lucide-react'
import { projectsApi, CreateProjectSystemError, ProjectSystem } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'

interface Props {
  // project the new project_system lives under. used for the Phase 3.5 AI
  // function suggestion call (POST /api/projects/:projectId/suggest-function
  // reads that project's function_name taxonomy as the candidate list)
  projectId: string
  // the unmapped Word RA assessment that becomes the source of truth for
  // the new project_system. system_name + supplier + ship_function drive
  // the preview. ship_function pre-fills the editable function_name field
  // (can be empty when the doc omits it. Sewage Treatment in SN2729 was
  // the finding)
  assessment: {
    id:            string
    system_name:   string
    supplier:      string | null
    ship_function: string | null
  }
  onClose:   () => void
  // fired on success with the new project_system row. caller refetches its
  // listings (AI Suggestions pane drops the row from pending, Mappings
  // panel reloads the dropdown options)
  onCreated: (ps: ProjectSystem) => void
  // fired when the user picks "Map to this instead" from the
  // DUPLICATE_SYSTEM modal swap. caller runs the existing mapping PATCH so
  // the assessment lands on the conflicting row
  onMapToExisting: (existingPsId: string) => void
}

export const CreateProjectSystemModal: React.FC<Props> = ({
  projectId, assessment, onClose, onCreated, onMapToExisting,
}) => {
  const { success, error: showError } = useToast()
  const docValue = (assessment.ship_function ?? '').trim()
  const [functionName, setFunctionName] = useState(assessment.ship_function ?? '')
  const [busy,         setBusy]         = useState(false)
  // Phase 3.5 AI function suggestion state. aiSuggesting drives the shimmer
  // on the input while we wait. aiSuggestion keeps what the server returned
  // so we can detect "user kept it as-is" on submit for the function_source
  // audit tag
  const [aiSuggesting, setAiSuggesting] = useState(false)
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null)
  // true the moment the user touches the input. flips function_source from
  // 'doc'/'ai' to 'manual' regardless of what the input now contains
  const userEditedRef = useRef(false)
  // on DUPLICATE_SYSTEM, the modal swaps to a single-screen "this already
  // exists" view offering Map / Cancel. existing carries the conflicting
  // row so the affordance shows what they're about to map to
  const [existing,     setExisting]     = useState<CreateProjectSystemError['existing_project_system']>(null)

  // fire the AI suggest call on mount only if the doc didn't supply a
  // ship_function. doc-supplied values stay authoritative. don't silently
  // overwrite them with an AI guess
  useEffect(() => {
    if (docValue) return
    let cancelled = false
    setAiSuggesting(true)
    projectsApi
      .suggestFunctionForSystem(projectId, {
        system_name: assessment.system_name,
        supplier:    assessment.supplier,
      })
      .then(result => {
        if (cancelled) return
        if (result.suggestion && !userEditedRef.current) {
          // user hasn't touched the input in the meantime, safe to
          // pre-fill. (race window: between mount and the suggest call
          // returning the user could type. skip the pre-fill if they have,
          // so we don't clobber their input)
          setAiSuggestion(result.suggestion)
          setFunctionName(result.suggestion)
        }
      })
      .finally(() => { if (!cancelled) setAiSuggesting(false) })
    return () => { cancelled = true }
  }, [projectId, assessment.system_name, assessment.supplier, docValue])

  const handleSubmit = async () => {
    setBusy(true)
    try {
      const trimmed = functionName.trim()
      // function_source derivation for the audit row:
      //   'manual' - user touched the input (whatever the final value)
      //   'ai'     - user kept the AI suggestion as-is
      //   'doc'    - user kept the doc's ship_function as-is
      // falls back to 'manual' when none of the three match (e.g. user
      // opened modal, did nothing, but doc was empty AND AI returned null,
      // submitting blank)
      let function_source: 'doc' | 'ai' | 'manual'
      if (userEditedRef.current) {
        function_source = 'manual'
      } else if (aiSuggestion && trimmed === aiSuggestion) {
        function_source = 'ai'
      } else if (docValue && trimmed === docValue) {
        function_source = 'doc'
      } else {
        function_source = 'manual'
      }

      const ps = await projectsApi.createProjectSystemFromAssessment(assessment.id, {
        function_name:   trimmed === '' ? null : trimmed,
        function_source,
      })
      success(`Created: "${ps.system_name}"`)
      onCreated(ps)
    } catch (err) {
      const e = err as CreateProjectSystemError
      if (e.code === 'DUPLICATE_SYSTEM' && e.existing_project_system) {
        setExisting(e.existing_project_system)
        setBusy(false)
        return
      }
      if (e.code === 'ALREADY_MAPPED') {
        showError(e.message)
        onClose()
        return
      }
      showError(e.message ?? 'Failed to create system')
      setBusy(false)
    }
  }

  const handleMapInstead = () => {
    if (!existing) return
    onMapToExisting(existing.id)
  }

  // existing-row swap view ────────────────────────────────────────────
  if (existing) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-base font-semibold">A matching system already exists</h2>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="p-5 space-y-3 text-sm">
            <p className="text-slate-600 dark:text-slate-300">
              An inventory row with the same name and vendor is already on this project. You can map{' '}
              <span className="font-semibold">{assessment.system_name}</span> to it instead of creating a duplicate.
            </p>
            <div className="border border-slate-200 dark:border-slate-700 rounded p-3 bg-slate-50 dark:bg-slate-900/40">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {existing.system_name ?? '(unnamed)'}
                </span>
                {existing.source !== 'excel' && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                    {existing.source === 'word_ra' ? 'Word RA' : 'Split'}
                  </span>
                )}
              </div>
              {existing.vendor_name_raw && (
                <div className="text-[11px] text-slate-500">{existing.vendor_name_raw}</div>
              )}
            </div>
          </div>

          <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleMapInstead}>Map to this instead</Button>
          </div>
        </div>
      </div>
    )
  }

  // default create-preview view ────────────────────────────────────
  // Phase 3.5 hint logic for the Function field:
  //   • doc supplied ship_function → no hint (default state)
  //   • AI suggested + user hasn't edited → "✨ AI suggested", subtle
  //     violet accent
  //   • doc empty + AI pending → shimmer on the input
  //   • doc empty + AI returned null + no edit → "doc omitted" hint
  const showAiHint   = !!aiSuggestion && !userEditedRef.current && functionName === aiSuggestion
  const showDocHint  = !docValue && !aiSuggesting && !aiSuggestion && !userEditedRef.current
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Plus className="h-4 w-4 text-emerald-500" />
            Create system from Word RA
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          <p className="text-[12px] text-slate-500 dark:text-slate-400">
            The Word RA system below will become a new inventory row. Name and vendor come from the Word doc verbatim — the function can be edited.
          </p>

          <PreviewRow label="System name (verbatim)"   value={assessment.system_name} />
          <PreviewRow label="Vendor (from supplier)"   value={assessment.supplier ?? '(none)'} />

          <div>
            <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-300 mb-1">
              Function
              {showDocHint && (
                <span className="ml-2 text-[10px] text-slate-400 normal-case">
                  doc omitted — fill in if known
                </span>
              )}
              {showAiHint && (
                <span className="ml-2 text-[10px] text-violet-500 normal-case inline-flex items-center gap-1">
                  <Sparkles className="h-2.5 w-2.5" />
                  AI suggested — edit if wrong
                </span>
              )}
            </label>
            <input
              value={functionName}
              onChange={e => { userEditedRef.current = true; setFunctionName(e.target.value) }}
              onKeyDown={e => { if (e.key === 'Enter' && !busy && !aiSuggesting) handleSubmit() }}
              disabled={aiSuggesting}
              className={`w-full text-sm px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white
                ${aiSuggesting ? 'animate-pulse' : ''}`}
              placeholder={aiSuggesting ? 'Thinking…' : 'e.g. Navigational systems'}
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          {/* Submit deliberately NOT gated on aiSuggesting. spec says
              "Create button never gated on the AI call" */}
          <Button size="sm" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Creating…' : 'Create system'}
          </Button>
        </div>
      </div>
    </div>
  )
}

const PreviewRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline gap-3 text-[12px]">
    <div className="text-slate-500 dark:text-slate-400 w-44 shrink-0">{label}</div>
    <div className="font-medium text-slate-900 dark:text-slate-100">{value}</div>
  </div>
)
